import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider
} from '@mui/material';
import { Home, Add, Edit, Delete, PostAdd, Book } from '@mui/icons-material';
import axios from 'axios';

const STATUS_COLOR = { Open: 'primary', Posted: 'success' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value ?? <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const ItemJournalList = () => {
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [confirm,  setConfirm]  = useState({ open: false, title: '', message: '', onOk: null });
  const [viewJournal, setViewJournal] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 1500);
    return () => clearTimeout(t);
  }, [success]);

  const fetchJournals = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payables/item-journals`, { headers }
      );
      setJournals(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load item journals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJournals(); }, []); // eslint-disable-line

  const openConfirm = (title, message, onOk) =>
    setConfirm({ open: true, title, message, onOk });
  const closeConfirm = () =>
    setConfirm({ open: false, title: '', message: '', onOk: null });

  const handlePost = (journalNo) => {
    openConfirm(
      `Post ${journalNo}`,
      `Posting will create Item Ledger Entries and GL postings for all lines in ${journalNo}. This cannot be undone.`,
      async () => {
        closeConfirm();
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/payables/item-journals/${journalNo}/post`,
            {}, { headers }
          );
          navigate(-1);
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to post journal');
        }
      }
    );
  };

  const handleDelete = (journalNo) => {
    openConfirm(
      `Delete ${journalNo}`,
      `Are you sure you want to delete Item Journal ${journalNo}?`,
      async () => {
        closeConfirm();
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/payables/item-journals/${journalNo}`, { headers }
          );
          setSuccess('Journal deleted');
          fetchJournals();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to delete journal');
        }
      }
    );
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading item journals...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5" color="primary">Item Journals</Typography>
            <Box>
              <IconButton onClick={() => navigate('/dashboard')} title="Dashboard"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/item-journals/new')} title="New Journal"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {journals.length === 0 ? (
            <Typography color="text.secondary">No item journals yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Journal No</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journals.map(j => (
                  <TableRow key={j.journalNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewJournal(j)}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{j.journalNo}</TableCell>
                    <TableCell>
                      {j.journalDate ? new Date(j.journalDate).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell>{j.description}</TableCell>
                    <TableCell align="center">{j.lines?.length || 0}</TableCell>
                    <TableCell>
                      <Chip label={j.status}
                        color={STATUS_COLOR[j.status] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {j.status === 'Open' && (
                        <>
                          <IconButton size="small" color="success" title="Post"
                            onClick={() => handlePost(j.journalNo)} sx={{ mr: 0.5 }}>
                            <PostAdd fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="primary" title="Edit"
                            onClick={() => navigate(`/payables/item-journals/${j.journalNo}/edit`)} sx={{ mr: 0.5 }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Delete"
                            onClick={() => handleDelete(j.journalNo)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {j.status === 'Posted' && (
                        <Button size="small" variant="text" color="primary"
                          onClick={() => navigate(`/payables/item-journals/${j.journalNo}/edit`)}>
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Journal Card ── */}
      <Dialog open={Boolean(viewJournal)} onClose={() => setViewJournal(null)} maxWidth="sm" fullWidth>
        {viewJournal && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Book color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewJournal.journalNo}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewJournal.description || 'Item Journal'}</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewJournal.status}
                    color={STATUS_COLOR[viewJournal.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Journal Details</Typography>
              <InfoRow label="Journal No"  value={viewJournal.journalNo} />
              <InfoRow label="Date"        value={viewJournal.journalDate ? new Date(viewJournal.journalDate).toLocaleDateString() : ''} />
              <InfoRow label="Description" value={viewJournal.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Summary</Typography>
              <InfoRow label="Lines"  value={viewJournal.lines?.length || 0} />
              <InfoRow label="Status" value={viewJournal.status} />
            </DialogContent>
            <DialogActions>
              {viewJournal.status === 'Open' && (
                <>
                  <Button startIcon={<PostAdd />} color="success"
                    onClick={() => { setViewJournal(null); handlePost(viewJournal.journalNo); }}>
                    Post
                  </Button>
                  <Button startIcon={<Edit />}
                    onClick={() => { setViewJournal(null); navigate(`/payables/item-journals/${viewJournal.journalNo}/edit`); }}>
                    Edit
                  </Button>
                </>
              )}
              {viewJournal.status === 'Posted' && (
                <Button startIcon={<Edit />}
                  onClick={() => { setViewJournal(null); navigate(`/payables/item-journals/${viewJournal.journalNo}/edit`); }}>
                  View Lines
                </Button>
              )}
              <Button variant="outlined" onClick={() => setViewJournal(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirm.onOk}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemJournalList;
